import type { ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cx } from './utils';

export type AdminChartTone = 'accent' | 'success' | 'warning' | 'danger' | 'neutral';

export type AdminChartDatum = {
  label: string;
} & Record<string, number | string>;

export interface AdminChartSeries {
  key: string;
  label: string;
  tone?: AdminChartTone;
}

interface AdminChartBaseProps {
  title?: ReactNode;
  description?: ReactNode;
  data: AdminChartDatum[];
  series: AdminChartSeries[];
  className?: string;
  emptyLabel: ReactNode;
  valueSuffix?: string;
}

function seriesColor(tone: AdminChartTone = 'accent') {
  return `var(--ui-admin-chart-${tone})`;
}

function formatValue(value: unknown, suffix?: string) {
  if (typeof value === 'number') return `${value}${suffix ?? ''}`;
  return String(value ?? '');
}

type ChartAxisTickProps = {
  x?: number | string;
  y?: number | string;
  className?: string;
  fill?: string;
  index?: number;
  payload?: {
    value?: unknown;
  };
  textAnchor?: 'start' | 'middle' | 'end' | 'inherit';
  tickFormatter?: (value: unknown, index: number) => ReactNode;
  unit?: ReactNode;
  verticalAnchor?: 'start' | 'middle' | 'end';
};

const responsiveContainerProps = {
  width: '100%',
  height: '100%',
  minWidth: 0,
  minHeight: 0,
  initialDimension: {
    width: 320,
    height: 168,
  },
} as const;

function ChartAxisTick({
  x,
  y,
  className,
  fill = 'var(--m13-color-666)',
  index = 0,
  payload,
  textAnchor = 'middle',
  tickFormatter,
  unit,
  verticalAnchor = 'end',
}: ChartAxisTickProps) {
  const rawValue = payload?.value ?? '';
  const value = tickFormatter ? tickFormatter(rawValue, index) : rawValue;
  const label = `${value ?? ''}${unit ?? ''}`;
  const dy = verticalAnchor === 'middle' ? '0.32em' : verticalAnchor === 'start' ? '0.71em' : 0;

  return (
    <text
      className={className}
      fill={fill}
      textAnchor={textAnchor}
      x={x}
      y={y}
      dy={dy}
    >
      {label}
    </text>
  );
}

const chartAxisTick = <ChartAxisTick />;

function AdminChartFrame({
  title,
  description,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('ui-admin-chart', className)}>
      {title || description ? (
        <header className="ui-admin-chart__header">
          {title ? <h4 className="ui-admin-chart__title">{title}</h4> : null}
          {description ? <p className="ui-admin-chart__description">{description}</p> : null}
        </header>
      ) : null}
      <div className="ui-admin-chart__plot">{children}</div>
    </section>
  );
}

export function AdminLineChart({
  title,
  description,
  data,
  series,
  className,
  emptyLabel,
  valueSuffix,
}: AdminChartBaseProps) {
  return (
    <AdminChartFrame title={title} description={description} className={className}>
      {data.length > 0 ? (
        <ResponsiveContainer {...responsiveContainerProps}>
          <LineChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="var(--ui-admin-chart-grid)" strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              interval={0}
              tick={chartAxisTick}
              tickLine={false}
              axisLine={{ stroke: 'var(--border-default)' }}
            />
            <YAxis
              interval={0}
              tick={chartAxisTick}
              tickFormatter={(value) => formatValue(value, valueSuffix)}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-raised)',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                color: 'var(--text-primary)',
              }}
              formatter={(value, name) => [formatValue(value, valueSuffix), name]}
            />
            <Legend wrapperStyle={{ paddingTop: '8px' }} />
            {series.map((item) => (
              <Line
                key={item.key}
                type="monotone"
                dataKey={item.key}
                name={item.label}
                stroke={seriesColor(item.tone)}
                dot={false}
                isAnimationActive={false}
                strokeWidth={2}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="ui-admin-chart__empty">{emptyLabel}</div>
      )}
    </AdminChartFrame>
  );
}

export function AdminBarChart({
  title,
  description,
  data,
  series,
  className,
  emptyLabel,
  valueSuffix,
}: AdminChartBaseProps) {
  return (
    <AdminChartFrame title={title} description={description} className={className}>
      {data.length > 0 ? (
        <ResponsiveContainer {...responsiveContainerProps}>
          <BarChart data={data} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid stroke="var(--ui-admin-chart-grid)" strokeDasharray="3 3" />
            <XAxis
              dataKey="label"
              interval={0}
              tick={chartAxisTick}
              tickLine={false}
              axisLine={{ stroke: 'var(--border-default)' }}
            />
            <YAxis
              interval={0}
              tick={chartAxisTick}
              allowDecimals={false}
              tickFormatter={(value) => formatValue(value, valueSuffix)}
              tickLine={false}
              axisLine={false}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--bg-raised)',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                color: 'var(--text-primary)',
              }}
              formatter={(value, name) => [formatValue(value, valueSuffix), name]}
            />
            <Legend wrapperStyle={{ paddingTop: '8px' }} />
            {series.map((item) => (
              <Bar
                key={item.key}
                dataKey={item.key}
                name={item.label}
                fill={seriesColor(item.tone)}
                isAnimationActive={false}
                radius={[4, 4, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="ui-admin-chart__empty">{emptyLabel}</div>
      )}
    </AdminChartFrame>
  );
}
